'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { supabase } from '@/lib/supabaseClient';

interface HomepageInteractiveProps {
  className?: string;
}

interface FormData {
  propertyAddress: string;
  name: string;
  phoneNumber: string;
  email: string;
}

const HomepageInteractive = ({ className = '' }: HomepageInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    propertyAddress: '',
    name: '',
    phoneNumber: '',
    email: '',
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.propertyAddress.trim()) {
      newErrors.propertyAddress = 'Property address is required';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const nameParts = formData.name.trim().split(/\s+/);
      const firstName = nameParts.shift() || formData.name.trim();
      const lastName = nameParts.join(' ') || 'Not provided';

      const { error } = await supabase.from('seller_leads').insert({
        property_address: formData.propertyAddress.trim(),
        city: 'Not provided',
        state: 'Not provided',
        zip_code: 'Not provided',
        first_name: firstName,
        last_name: lastName,
        email: formData.email.trim(),
        phone: formData.phoneNumber.trim(),
        situation: 'Homepage cash offer request',
        timeframe: 'ASAP',
        preferred_contact_method: 'phone',
        additional_details: 'Submitted from the homepage Get My Offer form.',
      });

      if (error) {
        throw error;
      }

      sessionStorage.setItem('initialLeadData', JSON.stringify(formData));
      router.push('/thank-you');
    } catch (err) {
      console.error('Error submitting homepage lead:', err);
      setSubmitError('Something went wrong while sending your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  if (!isHydrated) {
    return (
      <div className={`min-h-screen bg-background ${className}`}>
        <div className="animate-pulse">
          <div className="h-screen bg-surface"></div>
        </div>
      </div>
    );
  }

  // Reusable input class for the luxury inquiry panel.
  const inputClass = (hasError: boolean) =>
    `w-full border-0 border-b bg-transparent px-0 py-2.5 text-[15px] italic text-foreground placeholder:text-muted-foreground/70 outline-none transition-colors focus:border-primary focus:ring-0 ${
      hasError ? 'border-destructive' : 'border-border'
    }`;

  return (
    <div className={`min-h-screen bg-background ${className}`}>
      <div className="relative">
        <div className="px-6 pb-24 pt-16 lg:pb-28 lg:pt-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-16 items-center">
              {/* Left Column */}
              <div>
                <div className="mb-20 h-px w-20 bg-accent"></div>

                <div className="mb-8 font-heading text-xl italic text-primary">
                  — For Discerning Homeowners
                </div>

                {/* Headline */}
                <h1 className="max-w-[700px] text-[56px] md:text-[76px] lg:text-[88px] font-normal leading-[0.95] text-foreground mb-7">
                  A graceful exit
                  <br />
                  from your <span className="italic text-plum">property.</span>
                </h1>

                {/* Lede */}
                <p className="max-w-[610px] font-heading text-[22px] leading-[1.55] text-muted-foreground mb-14">
                  A fair, written cash offer in twenty-four hours. No repairs, no commissions, no
                  theatrics — close on your timeline, in 30 days or less.
                </p>

                <div className="mb-12 flex items-center gap-4 text-accent">
                  <span className="h-px w-10 bg-accent"></span>
                  <span>✦</span>
                  <span className="h-px w-10 bg-accent"></span>
                </div>

                <ul className="space-y-6 text-[16px]">
                  {[
                    ['No repairs needed.', 'We purchase properties precisely as they stand.'],
                    ['No fees or commissions.', 'The offer you receive is the cash you keep.'],
                    ['Close on your timeline.', '30 days or less, or at the pace you choose.'],
                  ].map(([strong, text]) => (
                    <li key={strong} className="flex items-start gap-7">
                      <span className="mt-1 text-xl text-accent">✦</span>
                      <span className="text-foreground">
                        <strong className="font-bold text-plum">{strong}</strong> {text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right Column - Form */}
              <div className="relative">
                <div className="relative border border-border bg-card/70 p-8 shadow-card md:p-11">
                  <span className="absolute left-3 top-3 h-7 w-7 border-l border-t border-accent"></span>
                  <span className="absolute bottom-3 right-3 h-7 w-7 border-b border-r border-accent"></span>

                  <div className="mb-6 text-center text-[11px] font-bold uppercase tracking-[0.34em] text-accent">
                    — Confidential Inquiry —
                  </div>

                  <h2 className="mb-2 text-center font-heading text-[34px] font-normal leading-tight text-foreground md:text-[42px]">
                    Request Your <span className="italic text-plum">Cash Offer</span>
                  </h2>
                  <p className="mb-8 text-center font-heading text-base italic text-muted-foreground">
                    A senior associate will respond within one business day.
                  </p>

                  <div className="mb-8 flex items-center gap-4 text-accent">
                    <span className="h-px flex-1 bg-border"></span>
                    <span>✦</span>
                    <span className="h-px flex-1 bg-border"></span>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label
                        htmlFor="propertyAddress"
                        className="block text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground"
                      >
                        Property Address
                      </label>
                      <input
                        type="text"
                        id="propertyAddress"
                        name="propertyAddress"
                        value={formData.propertyAddress}
                        onChange={handleChange}
                        placeholder="The address of your property"
                        className={inputClass(!!errors.propertyAddress)}
                      />
                      {errors.propertyAddress && (
                        <p className="mt-1 text-xs text-destructive">{errors.propertyAddress}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="name"
                        className="block text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground"
                      >
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className={inputClass(!!errors.name)}
                      />
                      {errors.name && (
                        <p className="mt-1 text-xs text-destructive">{errors.name}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="phoneNumber"
                        className="block text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground"
                      >
                        Telephone
                      </label>
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        placeholder="A number to reach you"
                        className={inputClass(!!errors.phoneNumber)}
                      />
                      {errors.phoneNumber && (
                        <p className="mt-1 text-xs text-destructive">{errors.phoneNumber}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground"
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Your email"
                        className={inputClass(!!errors.email)}
                      />
                      {errors.email && (
                        <p className="mt-1 text-xs text-destructive">{errors.email}</p>
                      )}
                    </div>

                    {submitError && (
                      <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                        {submitError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="mt-3 w-full bg-plum py-4 text-[12px] font-bold uppercase tracking-[0.34em] text-primary-foreground transition-colors hover:bg-plum-deep disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
                    </button>

                    <p className="pt-1 text-center font-heading text-sm italic text-muted-foreground">
                      All correspondence held in strictest confidence.
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust signals strip */}
      <div className="border-t border-border bg-surface">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="grid grid-cols-2 gap-8 text-center md:grid-cols-4">
            <span>
              <span className="block font-heading text-3xl text-plum md:text-4xl">
                Private Property Review
              </span>
              <span className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
                confidential
              </span>
            </span>
            <span>
              <span className="block font-heading text-3xl text-plum md:text-4xl">
                Flexible Closing Timeline
              </span>
              <span className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
                on your schedule
              </span>
            </span>
            <span>
              <span className="block font-heading text-3xl text-plum md:text-4xl">
                No Pressure Consultation
              </span>
              <span className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
                no obligation
              </span>
            </span>
            <span>
              <span className="block font-heading text-3xl text-plum md:text-4xl">
                Straightforward Guidance
              </span>
              <span className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
                clear next steps
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomepageInteractive;
